/**
 * AgentSelectorWidget — new-arch replacement for components/AgentSelector.tsx.
 *
 * Two-section card grid:
 *   Section 1  Accessible agents  — "Open" button, whole card clickable.
 *   Section 2  Locked agents      — "Request access" or "Request pending".
 */

import { useMemo, useState } from 'react';
import { Lock, Check } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogDescription,
  DialogTitle,
} from '@/shared/ui/dialog';
import { cn } from '@/shared/lib/cn';
import { useAgentCatalog } from '@/app/providers/AgentCatalogProvider';
import { useCapabilities } from '@/app/providers/CapabilitiesProvider';
import { readEnv } from '@/shared/config';
import type { AgentView, DeniedAgentView } from '@/entities/agent';
import { DevStatusBadge } from '@/entities/agent/ui/DevStatusBadge';
import argusLogo from '../../../assets/images/acs-argus-mark-192.png';

type AgentTile = {
  agentId: string;
  displayName: string;
  bullets: ReadonlyArray<string>;
  requiredGroups: ReadonlyArray<string>;
  accountType: 'single' | 'cross' | 'solution' | 'orchestrator' | null;
  devStatus: 'live' | 'experimental' | 'development' | 'planned' | null;
  accessible: boolean;
  requestPending: boolean;
};

function toAccessibleTile(agent: AgentView): AgentTile {
  return {
    agentId: agent.id,
    displayName: agent.displayName,
    bullets: agent.bullets ?? [],
    requiredGroups: [],
    accountType: agent.accountType ?? null,
    devStatus: agent.devStatus ?? null,
    accessible: true,
    requestPending: false,
  };
}

function toDeniedTile(denied: DeniedAgentView, requestPending: boolean): AgentTile {
  return {
    agentId: denied.id,
    displayName: denied.displayName,
    bullets: denied.bullets ?? [],
    requiredGroups: denied.requiredGroups,
    accountType: denied.accountType ?? null,
    devStatus: denied.devStatus ?? null,
    accessible: false,
    requestPending,
  };
}

const getAccountTypeLabel = (accountType: Exclude<AgentTile['accountType'], null>): string => {
  if (accountType === 'solution') return 'Solution Level Insights';
  if (accountType === 'single') return 'Single Account Deep-Dive';
  if (accountType === 'orchestrator') return 'agent orchestrator';
  return 'Cross Account Analysis';
};

const BY_NAME = (a: AgentTile, b: AgentTile) =>
  a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' });

const CARD_GRID = 'grid grid-cols-[repeat(auto-fit,minmax(18rem,22rem))] justify-start gap-5';

const isArgusTile = (tile: AgentTile) =>
  tile.agentId === 'argus' || tile.displayName.toLowerCase() === 'argus' || tile.accountType === 'orchestrator';

type SubAgentStatus = { available: number; total: number };

type AgentCardProps = {
  tile: AgentTile;
  onOpen: () => void;
  subAgentStatus?: SubAgentStatus;
};

function AgentCard({ tile, onOpen, subAgentStatus }: AgentCardProps) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const cardAction = tile.accessible ? onOpen : null;
  const isArgus = isArgusTile(tile);

  return (
    <Card
      size="sm"
      className={cn(
        'group w-full select-none overflow-hidden border border-border outline-none transition-shadow',
        'hover:shadow-sm',
        cardAction && 'cursor-pointer',
        !tile.accessible && 'opacity-90',
        isArgus ? 'min-h-[15rem] shadow-sm' : 'min-h-[13.5rem]',
      )}
      onClick={cardAction ?? undefined}
      role={cardAction ? 'button' : undefined}
      aria-label={cardAction ? tile.displayName : undefined}
      tabIndex={cardAction ? 0 : undefined}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && cardAction) { e.preventDefault(); cardAction(); }
      }}
    >
      <CardHeader className="gap-2 border-b border-border/60">
        <div className="flex min-w-0 items-start gap-3">
          {isArgus && <img src={argusLogo} alt="" aria-hidden className="size-8 shrink-0 object-contain" />}
          <CardTitle
            className={cn(
              'min-w-0 flex-1 leading-snug font-semibold text-card-foreground [overflow-wrap:anywhere]',
              isArgus ? 'text-lg' : 'text-base',
            )}
          >
            {tile.displayName}
          </CardTitle>
          {tile.devStatus && <DevStatusBadge status={tile.devStatus} />}
        </div>
        {tile.accountType && (
          <CardDescription className="text-xs font-medium uppercase tracking-[0.06em]">
            {getAccountTypeLabel(tile.accountType)}
          </CardDescription>
        )}
      </CardHeader>

      <CardContent className="flex min-h-0 flex-1 flex-col pt-3">
        <ul className="flex flex-1 flex-col gap-1 text-sm leading-5 text-muted-foreground [overflow-wrap:anywhere]">
          {tile.bullets.slice(0, 5).map((bullet) => (
            <li key={bullet} className="flex items-start gap-2">
              <span
                className="mt-0.5 inline-flex size-4 shrink-0 items-center text-muted-foreground [&>svg]:size-4"
                aria-hidden
              >
                <Check />
              </span>
              <span className={cn(isArgus ? 'line-clamp-2' : 'line-clamp-2')}>{bullet}</span>
            </li>
          ))}
        </ul>

        {subAgentStatus && (
          <div className="mt-3 flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <span className="relative flex size-[7px] shrink-0" aria-hidden>
                <span className="absolute inset-0 animate-ping rounded-full bg-status-success opacity-75" />
                <span className="relative inline-flex size-full rounded-full bg-status-success" />
              </span>
              {subAgentStatus.available}/{subAgentStatus.total} agents available
            </span>
          </div>
        )}
      </CardContent>

      <CardFooter
        className="mt-auto p-3"
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span
            className={cn(
              'inline-flex size-5 shrink-0 items-center text-muted-foreground [&>svg]:size-[18px]',
              isArgus && 'hidden',
            )}
          >
            {!tile.accessible && <Lock aria-label="Access restricted" />}
          </span>
          <div className={cn('flex-1', isArgus && 'hidden')} />
          {tile.accessible ? (
            <Button onClick={onOpen} className={cn(subAgentStatus && 'w-full justify-center text-center')}>
              Open Agent
            </Button>
          ) : tile.requiredGroups.length === 0 ? (
            <Button disabled>Coming Soon</Button>
          ) : (
            <>
              <Button onClick={() => setDialogOpen(true)}>
                Request Access
              </Button>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Request Access - {tile.displayName}</DialogTitle>
                    <DialogDescription>
                      Subscribe to the below group in the IAM portal for access.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex flex-col gap-3 text-sm">
                    {tile.requestPending ? (
                      <p className="text-muted-foreground">
                        Your access request is pending.
                      </p>
                    ) : null}
                    <div className="rounded-lg border border-border bg-muted/40 p-3">
                      <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">Required group</p>
                      <p className="mt-2 text-sm text-foreground">{tile.requiredGroups.join(', ')}</p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="secondary" onClick={() => setDialogOpen(false)}>Close</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </CardFooter>
    </Card>
  );
}

type AgentSelectorWidgetProps = {
  selectedAgentId: string | null;
  onAgentSelect: (agentId: string) => void;
  onActivateAgent?: (agentId: string) => void;
};

export function AgentSelectorWidget({
  selectedAgentId,
  onAgentSelect,
  onActivateAgent,
}: AgentSelectorWidgetProps) {
  const appEnv = readEnv('VITE_APP_ENV');
  const isLocal = appEnv !== undefined ? appEnv === 'local' : import.meta.env.DEV;
  const { agents, deniedAgents, agentsLoading, agentsError } = useAgentCatalog();
  const { capabilities } = useCapabilities();

  // Build sorted tile list for a given account-type filter (null = all types).
  // Orchestrator tiles always sort before all other tiles within any group.
  const sortedTiles = useMemo(() => {
    const pendingGroups = new Set(capabilities?.pending_iam_groups ?? []);
    const allTiles: AgentTile[] = [
      ...agents.map(toAccessibleTile),
      ...deniedAgents.map((d) => toDeniedTile(d, d.requiredGroups.some((g) => pendingGroups.has(g)))),
    ];

    const isOrchestrator = (t: AgentTile) => t.accountType === 'orchestrator';

    const forType = (type: AgentTile['accountType'] | 'all'): AgentTile[] => {
      const filtered = type === 'all' ? allTiles : allTiles.filter((t) => t.accountType === type);
      const orchestratorTiles = filtered.filter(isOrchestrator).sort(BY_NAME);
      const accessible = filtered.filter((t) => t.accessible && !isOrchestrator(t)).sort(BY_NAME);
      const denied = filtered.filter((t) => !t.accessible && !isOrchestrator(t)).sort(BY_NAME);
      return [...orchestratorTiles, ...accessible, ...denied];
    };

    return {
      orchestrator: allTiles.filter(isOrchestrator).sort(BY_NAME),
      all: forType('all'),
      locked: allTiles.filter((t) => !t.accessible).sort(BY_NAME),
      cross: forType('cross'),
      single: forType('single'),
      solution: forType('solution'),
    };
  }, [agents, deniedAgents, capabilities]);

  const hasAnyAgents = sortedTiles.all.length > 0;

  const openAgent = (agentId: string) => {
    if (onActivateAgent) {
      onActivateAgent(agentId);
      return;
    }
    onAgentSelect(agentId);
  };

  if (selectedAgentId) return <div className="shrink-0" data-testid="agent-selector-shrink" />;

  if (agentsLoading) {
    return (
      <div className="flex flex-1 shrink-0 flex-col overflow-hidden">
        <div className="flex flex-1 flex-col overflow-auto bg-background px-6 pt-6 pb-8">
          <div className="mb-6">
            <h3 className="text-lg font-semibold">Loading agents...</h3>
          </div>
        </div>
      </div>
    );
  }

  if (agentsError) {
    return (
      <div className="flex flex-1 shrink-0 flex-col overflow-hidden">
        <div className="flex flex-1 flex-col overflow-auto bg-background px-6 pt-6 pb-8">
          <div className="mb-6">
            <h3 className="text-lg font-semibold">Agents unavailable</h3>
          </div>
          {isLocal ? (
            <p>
              We could not load the agent catalog from the backend. Verify the API is running on
              `http://127.0.0.1:8000` and that the Vite proxy is reaching it.
            </p>
          ) : (
            <p>
              We could not load the agent catalog from the backend. Please try again or contact
              support if the problem persists.
            </p>
          )}
        </div>
      </div>
    );
  }

  const nonOrchestratorTiles = sortedTiles.all.filter((t) => t.accountType !== 'orchestrator');
  const subAgentStatus: SubAgentStatus = {
    available: nonOrchestratorTiles.filter((t) => t.accessible).length,
    total: nonOrchestratorTiles.length,
  };
  const argusTiles = sortedTiles.all.filter(isArgusTile);
  const standardTiles = sortedTiles.all.filter((tile) => !isArgusTile(tile));

  return (
    <div className="flex flex-1 shrink-0 flex-col overflow-hidden">
      <div className="flex flex-1 flex-col overflow-auto bg-background px-6 pt-6 pb-8">
        <p className="mb-5 text-lg font-semibold text-foreground">Click on an agent to get started</p>
        {!hasAnyAgents ? (
          <p>No agents are currently available for this user.</p>
        ) : (
          <div className="w-full max-w-[68.5rem]">
            <div className={CARD_GRID}>
              {argusTiles.map((tile) => (
                <div key={tile.agentId} className="col-span-full mb-1">
                  <AgentCard
                    tile={tile}
                    onOpen={() => openAgent(tile.agentId)}
                    subAgentStatus={subAgentStatus}
                  />
                </div>
              ))}
              {standardTiles.map((tile) => (
                <AgentCard
                  key={tile.agentId}
                  tile={tile}
                  onOpen={() => openAgent(tile.agentId)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
